import React, { useState, useEffect } from "react";
import { api, resolveImage } from "@/lib/api";

/**
 * Omkar Milk Distributor — professional logo component.
 * Automatically fetches and displays uploaded custom logo from database (whitelabel_settings),
 * falling back gracefully to SVG logo mark if no image uploaded.
 */
export default function OmkarLogo({ size = 36, withWordmark = false, className = "", dark = false }) {
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    api.get("/whitelabel")
      .then((res) => {
        if (res.data?.logo_url) {
          setLogoUrl(resolveImage(res.data.logo_url));
        }
      })
      .catch(() => {});
  }, []);

  return (
    <span className={`inline-flex items-center gap-3 ${className}`} aria-label="Omkar Milk Distributor">
      {logoUrl ? (
        <img
          src={logoUrl}
          alt="Company Logo"
          style={{ width: size, height: size }}
          className="object-contain shrink-0"
        />
      ) : (
        <svg
          width={size}
          height={size}
          viewBox="0 0 64 64"
          role="img"
          aria-hidden="true"
          className="shrink-0"
        >
          <defs>
            <linearGradient id={`omkBg-${size}`} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#0047AB" />
              <stop offset="100%" stopColor="#003380" />
            </linearGradient>
            <linearGradient id={`omkDrop-${size}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#FFFFFF" />
              <stop offset="100%" stopColor="#DCE9FB" />
            </linearGradient>
          </defs>
          <rect width="64" height="64" rx="14" fill={`url(#omkBg-${size})`} />
          <path
            d="M32 12 C 24 24, 18 32, 18 40 C 18 48.8 24.4 54 32 54 C 39.6 54 46 48.8 46 40 C 46 32 40 24 32 12 Z"
            fill={`url(#omkDrop-${size})`}
          />
          <ellipse cx="27.5" cy="38" rx="3" ry="6" fill="#FFFFFF" opacity="0.55" />
          <circle cx="32" cy="42" r="5.5" fill="none" stroke="#0047AB" strokeWidth="2" />
          <circle cx="32" cy="42" r="1.6" fill="#0047AB" />
        </svg>
      )}
      {withWordmark && (
        <span className="flex flex-col leading-tight">
          <span
            className={`font-heading font-bold tracking-tight ${dark ? "text-white" : "text-slate-900"}`}
            style={{ fontSize: Math.max(14, size * 0.45) }}
          >
            Omkar Milk
          </span>
          <span
            className={`uppercase tracking-[0.22em] font-semibold ${dark ? "text-white/70" : "text-slate-500"}`}
            style={{ fontSize: Math.max(8, size * 0.22) }}
          >
            Distributor
          </span>
        </span>
      )}
    </span>
  );
}
